import React from "react";
import { PageHeader, PhxBadge, EmptyState } from "@/design-system";
import { useAuth } from "@/context/AuthContext";

/**
 * Sprint 10 Foundation landing page.
 *
 * Renders inside AppShell. Shows the current user's resolved roles and
 * permissions so operators and QA can verify the backend contract.
 * Replaces the Sprint-2 "protected routes probe" grid in a cleaner way.
 */
export default function FoundationHome() {
  const { user, roles, permissions } = useAuth();

  return (
    <div data-testid="foundation-home">
      <PageHeader
        eyebrow="Sprint 10 · Foundation"
        title={`Welcome, ${user?.name || "operator"}.`}
        description="You're inside the new Phoenix AppShell. Module screens will slot in here from Sprint 10.2 onward."
      />

      <section className="grid md:grid-cols-2 gap-6">
        <div className="border border-[var(--phx-slate-200)] bg-white p-6 rounded-[var(--phx-radius-md)]">
          <h2 className="text-xs uppercase tracking-[var(--phx-tracking)] font-semibold text-[var(--phx-slate-500)] mb-3">
            Your roles
          </h2>
          <div data-testid="foundation-roles" className="flex flex-wrap gap-2">
            {roles.length === 0 ? (
              <span className="text-sm text-[var(--phx-slate-500)]">No roles.</span>
            ) : (
              roles.map((r) => (
                <PhxBadge key={r} tone="primary" data-testid={`role-${r}`}>{r}</PhxBadge>
              ))
            )}
          </div>
        </div>

        <div className="border border-[var(--phx-slate-200)] bg-white p-6 rounded-[var(--phx-radius-md)]">
          <h2 className="text-xs uppercase tracking-[var(--phx-tracking)] font-semibold text-[var(--phx-slate-500)] mb-3">
            Your permissions ({permissions.length})
          </h2>
          {permissions.length === 0 ? (
            <EmptyState
              title="No permissions resolved."
              description="Assign a role to this account via /settings/roles when that module ships."
            />
          ) : (
            <div data-testid="foundation-permissions" className="flex flex-wrap gap-1 max-h-64 overflow-y-auto">
              {permissions.map((p) => (
                <code
                  key={p}
                  data-testid={`perm-${p}`}
                  className="text-[11px] font-mono bg-[var(--phx-slate-100)] text-[var(--phx-slate-700)] px-2 py-0.5 rounded-[var(--phx-radius-sm)]"
                >
                  {p}
                </code>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
